import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BackToOverview from 'core/components/BackToOverview';
import PageBackground from 'core/components/PageBackground';
import UserMenu from 'core/components/UserMenu';
import { selectAusgewaehlteIsin } from 'modules/aktien/features/aktienDbSlice';

/**
 * Vollbild-Layout der Aktienanalyse: linke Sidebar (Navigation) + Inhalt.
 *
 * Die eigentlichen Ansichten werden über verschachtelte Routen (<Outlet/>)
 * eingeblendet. Erweiterung: einfach einen weiteren NavLink + eine Route in
 * App.js ergänzen.
 */

const navBase =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors';

function SidebarLink({ to, end, icon, label, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `${navBase} ${
          isActive
            ? 'bg-ink text-paper'
            : 'text-ink-muted hover:bg-ink/5 hover:text-ink'
        }`
      }
    >
      <span className="text-lg" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </NavLink>
  );
}

function DisabledLink({ icon, label, hint = 'Bald verfügbar', badge = 'bald' }) {
  return (
    <div
      className={`${navBase} cursor-not-allowed text-ink-muted/60`}
      title={hint}
    >
      <span className="text-lg" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
      {badge && (
        <span className="ml-auto rounded-full bg-paper-sunken px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-ink-muted">
          {badge}
        </span>
      )}
    </div>
  );
}

/** Abschnitts-Überschrift in der Sidebar (z.B. „Übersicht", „Analyse"). */
function SectionLabel({ children }) {
  return (
    <p className="px-3 pb-1 pt-2 font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
      {children}
    </p>
  );
}

/** Inhalt der Sidebar (Navigation), geteilt von Desktop-Leiste und Handy-Schublade. */
function SidebarContent({ ausgewaehlteIsin, onNavigate }) {
  return (
    <>
      <BackToOverview className="mb-6 self-start" onClick={onNavigate} />

      <div className="mb-6 flex items-center gap-2 px-2">
        <span className="text-2xl">📈</span>
        <span className="font-serif text-xl font-semibold text-ink">Aktienanalyse</span>
      </div>

      <nav className="flex flex-col gap-1">
        {/* Übersicht: persönliche Sammlungen + Live-Indizes. */}
        <SectionLabel>Übersicht</SectionLabel>
        <SidebarLink to="/projekte/aktienanalyse/depot" icon="💼" label="Depot" onNavigate={onNavigate} />
        <SidebarLink to="/projekte/aktienanalyse/listen" icon="⭐" label="Meine Listen" onNavigate={onNavigate} />
        <SidebarLink to="/projekte/aktienanalyse" end icon="🗂️" label="Indizes" onNavigate={onNavigate} />

        {/* Analyse: Daten stammen aus der eigenen Datenbank. */}
        <div className="my-2 border-t border-line" />
        <SectionLabel>Analyse</SectionLabel>
        <SidebarLink to="/projekte/aktienanalyse/hinzufuegen" icon="➕" label="Aktien hinzufügen" onNavigate={onNavigate} />
        <SidebarLink to="/projekte/aktienanalyse/liste" icon="📋" label="Liste DB" onNavigate={onNavigate} />
        <SidebarLink to="/projekte/aktienanalyse/analyse" icon="🔬" label="Aktie Analyse" onNavigate={onNavigate} />
        {ausgewaehlteIsin ? (
          <SidebarLink
            to={`/projekte/aktienanalyse/aktie/${ausgewaehlteIsin}`}
            icon="📊"
            label="Aktie Details"
            onNavigate={onNavigate}
          />
        ) : (
          <DisabledLink
            icon="📊"
            label="Aktie Details"
            hint="Wähle zuerst eine Aktie in der Liste DB."
            badge={null}
          />
        )}

        <div className="my-3 border-t border-line" />
        <SectionLabel>News</SectionLabel>
        <SidebarLink
          to="/projekte/aktienanalyse/schwankungen"
          icon="⚡"
          label="Schwankungen"
          onNavigate={onNavigate}
        />
        <SidebarLink
          to="/projekte/aktienanalyse/berichte"
          icon="📄"
          label="Berichte"
          onNavigate={onNavigate}
        />
      </nav>

      <div className="mt-auto border-t border-line pt-4">
        <UserMenu variant="kompakt" />
      </div>
    </>
  );
}

export default function AktienanalyseLayout() {
  // ISIN der zuletzt angeklickten Aktie -> aktiviert den Reiter „Aktie Details".
  const ausgewaehlteIsin = useSelector(selectAusgewaehlteIsin);

  // Handy-Schublade: Zustand offen/geschlossen. Auf dem PC bleibt sie ungenutzt.
  const [schubladeOffen, setSchubladeOffen] = useState(false);

  // Hintergrund je Unterseite wählen (zentral hier, da der Inhalt in einem
  // transformierten Wrapper liegt und ein fixierter Hintergrund dort nicht
  // mehr zum Viewport, sondern zum Wrapper positioniert würde).
  const { pathname } = useLocation();
  const bgName = pathname.includes('/aktie/') ? 'detail' : 'aktien';

  // Bei jedem Seitenwechsel die Schublade schließen.
  useEffect(() => setSchubladeOffen(false), [pathname]);

  // Bei offener Schublade den Seiten-Hintergrund nicht mitscrollen lassen.
  useEffect(() => {
    document.body.style.overflow = schubladeOffen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [schubladeOffen]);

  return (
    <div className="flex min-h-screen overflow-x-clip">
      <PageBackground name={bgName} />

      {/* Handy: Topbar mit Hamburger-Button (nur unterhalb md sichtbar). */}
      <header className="fixed inset-x-0 top-0 z-30 flex items-center gap-3 border-b border-line bg-paper-soft/90 px-4 py-3 backdrop-blur-sm md:hidden">
        <button
          type="button"
          onClick={() => setSchubladeOffen(true)}
          aria-label="Menü öffnen"
          className="rounded-lg p-1.5 text-xl text-ink transition-colors hover:bg-ink/5"
        >
          ☰
        </button>
        <span className="text-xl">📈</span>
        <span className="font-serif text-lg font-semibold text-ink">Aktienanalyse</span>
      </header>

      {/* Handy: abdunkelnder Hintergrund hinter der offenen Schublade. */}
      {schubladeOffen && (
        <div
          className="fixed inset-0 z-30 bg-ink/30 backdrop-blur-sm md:hidden"
          onClick={() => setSchubladeOffen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: am PC fest, am Handy als einschiebbare Schublade. */}
      <aside
        className={`flex w-64 shrink-0 flex-col border-r border-line bg-paper-soft p-4 backdrop-blur-sm transition-transform
          max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-40 max-md:w-72 max-md:max-w-[85%] max-md:overflow-y-auto max-md:shadow-xl
          ${schubladeOffen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'}
          md:translate-x-0 md:bg-paper-soft/70`}
      >
        <SidebarContent
          ausgewaehlteIsin={ausgewaehlteIsin}
          onNavigate={() => setSchubladeOffen(false)}
        />
      </aside>

      {/* Inhalt: am Handy oben Platz für die Topbar lassen. `min-w-0` lässt das
          Flex-Item auf Handybreite schrumpfen, statt breite Tabellen die ganze
          Seite horizontal scrollen zu lassen. */}
      <main className="min-w-0 flex-1 px-4 pb-8 pt-20 sm:px-10 md:py-8">
        <div className="mx-auto w-full max-w-5xl animate-fade-in-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
